import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const SIZE_UNIT_STORAGE_KEY = 'size-unit-preference';

/**
 * Men's/Women's size-unit toggle for dual-gender shoe sizing ("M 4 / W 5.5").
 *
 * The server renders both numbers inside every size pill; this component only
 * flips the `data-size-unit` attribute on the closest `<variant-picker>` (CSS
 * shows the matching number) and persists the shopper's choice.
 *
 * The attribute survives section morphs (see morph.js), but morphs restore the
 * server-rendered `aria-pressed` defaults, so the stored preference is
 * re-applied after every `variant:update`.
 *
 * @extends {Component}
 */
class SizeUnitToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#apply(this.#storedUnit);
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#resync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section, dialog')?.removeEventListener(ThemeEvents.variantUpdate, this.#resync);
  }

  get #storedUnit() {
    try {
      return localStorage.getItem(SIZE_UNIT_STORAGE_KEY) === 'womens' ? 'womens' : 'mens';
    } catch {
      return 'mens';
    }
  }

  /**
   * Handles a click on one of the toggle buttons.
   * @param {MouseEvent} event - The click event.
   */
  select(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-unit]') : null;
    const unit = button?.dataset.unit;
    if (!unit) return;

    try {
      localStorage.setItem(SIZE_UNIT_STORAGE_KEY, unit);
    } catch {
      // Persistence unavailable (e.g. blocked storage) — still toggle for this page.
    }
    this.#apply(unit);
  }

  #resync = () => this.#apply(this.#storedUnit);

  /**
   * @param {string} unit - Either 'mens' or 'womens'.
   */
  #apply(unit) {
    this.closest('variant-picker')?.setAttribute('data-size-unit', unit);
    for (const button of this.querySelectorAll('button[data-unit]')) {
      button.setAttribute('aria-pressed', String(button.dataset.unit === unit));
    }
  }
}

if (!customElements.get('size-unit-toggle')) {
  customElements.define('size-unit-toggle', SizeUnitToggle);
}
